/* eslint-disable @typescript-eslint/no-explicit-any */

import { CategoryTypeId } from '@lib/features/categories/categories';
import { EntrySectionId, EntryTypeId } from '@lib/features/entries';
import { mapObject } from '@liquorice/allsorts-craftcms-nextjs';
import {
  EntryIndexQueryProps,
  EntryIndexQueryResult,
  getEntryIndexData,
} from './getEntryIndexData';

export const createEntryIndexDefaults = <
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
>(
  entrySection: E | E[],
  categoryType?: C | C[],
  options?: Partial<EntryIndexQueryProps<E, T, C>>
): EntryIndexQueryProps<E, T, C> => ({
  entrySection,
  categoryType,
  ...options,
});

// ------------------------------------------------------------------------------------------------
// ---- Create the config object ----

const entryIndexDefaults = {
  articleIndex: createEntryIndexDefaults('article', 'articleCategory'),
  profileIndex: createEntryIndexDefaults('profile', 'profileCategory'),
  eventIndex: createEntryIndexDefaults(
    'event',
    ['ageGroup', 'eventType', 'eventBrand', 'accessibility'],
    {
      orderBy: 'isEverydayEvent ASC, eventDateStart ASC, elements.id ASC',
      // entryType: 'default',
      indexDisplay: ['eventIndex', 'both'],
    }
  ),
  ticketIndex: createEntryIndexDefaults(
    'event',
    ['ageGroup', 'eventType', 'eventBrand', 'accessibility'],
    {
      orderBy: 'isEverydayEvent ASC, eventDateStart ASC, elements.id ASC',
      indexDisplay: ['ticketIndex', 'both'],
    }
  ),
  eventPerformanceIndex: createEntryIndexDefaults(
    'eventPerformance',
    ['ageGroup', 'eventType', 'eventBrand', 'accessibility'],
    {
      orderBy: 'isEverydayEvent ASC, eventDateStart ASC, elements.id ASC',
    }
  ),
  learningIndex: createEntryIndexDefaults('learning', ['learningLevel', 'learningCategory'], {
    orderBy: 'title',
  }),
  search: createEntryIndexDefaults(
    [
      'article',
      'event',
      'learning',
      'articleIndex',
      'eventIndex',
      'learningIndex',
      'page',
      'profile',
      'profileIndex',
    ],
    [],
    {
      perPage: 6,
      orderBy: 'score DESC',
    }
  ),
} as const;

// ------------------------------------------------------------------------------------------------
// ---- Extract the Types ----

type EntryIndexSettings = typeof entryIndexDefaults;

// export type EntryIndexKey = Extract<EntryIndexSectionId, keyof EntryIndexSettings>;
export type EntryIndexKey = keyof EntryIndexSettings;

export type EntryIndexEntryType<
  T extends EntryIndexKey,
  U extends EntryTypeId
> = EntryIndexSettings[T] extends EntryIndexQueryProps<infer E, U, any> ? E : never;

export type EntryIndexCategoryType<
  T extends EntryIndexKey,
  U extends EntryTypeId
> = EntryIndexSettings[T] extends EntryIndexQueryProps<any, U, infer C> ? C : never;

export type AppIndexData = {
  [P in EntryIndexKey]?: EntryIndexQueryResult<
    EntryIndexEntryType<P, EntryTypeId>,
    EntryTypeId,
    EntryIndexCategoryType<P, EntryTypeId>
  >;
};

export const getEntryIndexDefaults = <T extends EntryIndexKey>(key: T) =>
  entryIndexDefaults[key] as EntryIndexSettings[T];

// ------------------------------------------------------------------------------------------------
// ---- Create the Fetchers ----

export type EntryIndexCallbacks = {
  [P in EntryIndexKey]: (
    props: Partial<EntryIndexSettings[P]>
  ) => Promise<
    EntryIndexQueryResult<
      EntryIndexEntryType<P, EntryTypeId>,
      EntryTypeId,
      EntryIndexCategoryType<P, EntryTypeId>
    >
  >;
};

export const indexDataCallbacks = mapObject(entryIndexDefaults, (defaults) => {
  return async (props?: Partial<typeof defaults>) => {
    return await getEntryIndexData({
      ...defaults,
      ...props,
    });
  };
}) as EntryIndexCallbacks;

export const isEntryIndexKey = (x?: string): x is EntryIndexKey => !!x && x in entryIndexDefaults;
export const getIndexDataCallback = <T extends EntryIndexKey>(type: T) => indexDataCallbacks[type];
